"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FileText, ExternalLink, Download, X, Upload, Loader2 } from "lucide-react"
import { useGetFilesOwnerFileIdPresign, usePostFilesOwnerOwnerId } from "openapi/api/endpoints"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

interface ResumeViewerProps {
  candidateId?: string
  candidateName?: string
  onClose?: () => void
}

export function ResumeViewer({ candidateId, candidateName, onClose }: ResumeViewerProps) {
  const {data:resume}=useGetFilesOwnerFileIdPresign('candidate',candidateId as string,{})
  const latestResumeFile = (resume?.urls ?? [])
  .filter(file => file.name.toLowerCase().includes("resume"))
  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  [0];
  const {mutateAsync:uploadFile}=usePostFilesOwnerOwnerId()
  const queryClient=useQueryClient()
  const [localResumeUrl, setLocalResumeUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  
  useEffect(() => {
    if (latestResumeFile?.url) {
      setLocalResumeUrl(latestResumeFile.url);
    }
  }, [latestResumeFile?.url]);

  const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file only")
      return
    }

    setIsUploading(true)
    try {
      await uploadFile({
        owner:'candidate',
        data:{
          file,
          name:'resume'
        },
        ownerId:candidateId as string,
      })
      await queryClient.invalidateQueries({queryKey:[`/files`],exact:false})
      // Create a temporary URL for preview
      const tempUrl = URL.createObjectURL(file)
      setLocalResumeUrl(tempUrl)
      toast.success("File upload complete",{
        description: `${candidateName}'s resume has been added`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#a3e635'
        }
      })
    } catch (error) {
      console.error({UpladResumeErr:error})
      toast.error("An error occured!",{
        description: "Failed to upload file. Please try again",
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
    } finally {
      setIsUploading(false)
    }
  }

  const handleDownload = () => {
    if (!localResumeUrl) return

    // In real app, this would download from the actual storage URL
    const link = document.createElement("a")
    link.href = localResumeUrl
    link.download = `${candidateName?.replace(/\s+/g, "_")}_resume.pdf` || "resume.pdf"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOpenInNewTab = () => {
    if (!localResumeUrl) return
    window.open(localResumeUrl, "_blank")
  }

  return (
    /*
      Canvas 6a2. The artboard draws the CV as a serif page because it is a
      mockup; here there is a real PDF, so the document stays real and only the
      chrome around it follows the canvas — a toolbar naming the file, then the
      page floating on the dark surface with the canvas's shadow.

      No page indicator: an <object> embed does not report a page count, and a
      made-up "page 1 of 2" would be a lie about someone's CV.
    */
    <div className="flex h-full min-h-full flex-col rounded-[10px] border bg-card px-4 py-4 @2xl:px-6 @2xl:py-5">
      <div className="flex items-center gap-2.5 border-b pb-3.5">
        <FileText className="h-4 w-4 shrink-0 text-brand-ink" />
        <span className="min-w-0 truncate text-[12.5px] font-medium">
          {latestResumeFile?.name ?? `${candidateName ?? "Candidate"} CV`}
        </span>
        <span className="shrink-0 whitespace-nowrap rounded-full border border-primary/35 bg-primary/[0.08] px-[9px] py-[5px] text-[11.5px] text-brand-ink">
          resume
        </span>
        {localResumeUrl && (
          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="ml-auto shrink-0 whitespace-nowrap text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
          >
            Open original
          </button>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close resume"
            className="ml-1 shrink-0 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div>
        {localResumeUrl ? (
          <div className="space-y-5 py-5">
            {/* The page itself — centred, floating, with the canvas shadow. */}
            <div className="mx-auto aspect-[8.5/11] w-full max-w-[450px] overflow-hidden rounded-sm border bg-[#fdfdfb] shadow-[0_18px_50px_-14px_rgba(0,0,0,0.45)] dark:border-transparent dark:shadow-[0_18px_50px_-14px_rgba(0,0,0,0.7)]">
              <object
                data={localResumeUrl}
                type="application/pdf"
                className="w-full h-full"
                title={`Resume - ${candidateName || "Candidate"}`}
              >
                {/* Fallback for browsers that don't support embedded PDFs */}
                <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-[#0f1419]">
                  <FileText className="h-6 w-6 opacity-50" />
                  <p className="text-[12.5px] font-medium">
                    This browser cannot display the PDF inline
                  </p>
                  <button
                    type="button"
                    onClick={handleOpenInNewTab}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-black/15 px-3 text-[12px]"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open in a new tab
                  </button>
                </div>
              </object>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 border-t pt-3.5">
              <button
                type="button"
                onClick={handleOpenInNewTab}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in new tab
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
              >
                <Download className="h-3.5 w-3.5" />
                Download PDF
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => {
                  setLocalResumeUrl(null)
                }}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-destructive/30 px-3 text-[12px] text-destructive transition-colors hover:bg-destructive/10"
              >
                <Upload className="h-3.5 w-3.5" />
                Replace résumé
              </button>
            </div>
          </div>
        ) : (
          // Empty State with Upload
          <div className="space-y-3.5 py-6">
            <div>
              <h3 className="text-[13px] font-medium">No résumé on file</h3>
              <p className="mt-1 text-[12.5px] text-muted-foreground">
                Upload a PDF and it becomes the candidate&rsquo;s document of record.
              </p>
            </div>
            <div className="max-w-md">
              <div className="rounded-md border border-dashed p-6 transition-colors hover:border-primary/40">
                <Input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  id="resume-upload"
                  onChange={handleResumeUpload}
                  disabled={isUploading}
                />
                <Label htmlFor="resume-upload" className="cursor-pointer">
                  <div className="text-center">
                    {isUploading ? (
                      <>
                        <Loader2 className="mx-auto mb-2.5 h-5 w-5 animate-spin text-brand-ink" />
                        <p className="text-[12.5px] font-medium">Uploading résumé…</p>
                      </>
                    ) : (
                      <>
                        <FileText className="mx-auto mb-2.5 h-5 w-5 text-muted-foreground" />
                        <p className="text-[12.5px] font-medium">Click to upload</p>
                        <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                          PDF only · max 10MB
                        </p>
                      </>
                    )}
                  </div>
                </Label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
